/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./VIPRDecoratable',
	'./VIPRDataPoint',
	'./VIPRCatDataItem',
	'./VIPRContDataItem',
	'./VIPRCatContDataItem',
	'../../../apiHelpers/SlotAPIHelper'
], function(VIPRDecoratable, VIPRDataPoint, VIPRCatDataItem, VIPRContDataItem, VIPRCatContDataItem, SlotAPIHelper) {


	// We should ignore any dataItem that is not part of the vipr slots
	// Adding a tooltip slot caused an error in the constructor
	class VIPRDataSet extends VIPRDecoratable {
		constructor(datasetId, queryResult, slots, context, content) {
			super();

			this.id = datasetId;
			this.queryResult = queryResult;
			this.slots = slots;
			this.context = context;
			this.context.queryResult = queryResult;
			this.context.dataset = this;
			this.viprDataPoints = {};

			// Need to preload dataItems to handle empty result.
			this.dataItems = [];
			const resultDataItems = queryResult.getResultItemList();
			const numberOfDataItems = resultDataItems ? resultDataItems.length : 0;

			for (let index = 0; index < numberOfDataItems; index++) {
				const resultDataItem = resultDataItems[index];
				// properly resolve the slot by looking for the dataitem in the slots.
				// CADBLOC-1504: SparkLine View for KPI is a line chart we built on fly, the temp dataItem id for it couldn't be used to find a slot, in this scenario, we find slot based on index
				const slot = this._getSlot(resultDataItem) || this.slots[index];
				const slotDef = slot && slot.getDefinition();
				const slotDataItems = slot && slot.getDataItemList();
				if (slotDataItems && slotDataItems.length) {
					// @todo check for multi measure case?
					const type = slotDataItems[0].getType();	// sample the type with the first dataitem
					let VIPRDataItem = type === 'attribute' ? VIPRCatDataItem : VIPRContDataItem;

					const latLongSubTypes = ['latitude', 'longitude'];
					if (latLongSubTypes.indexOf(slotDef.getSubType()) > -1) {
						VIPRDataItem = VIPRCatContDataItem;
					}
					this.context.index = {
						dataitem: this.slots.indexOf(slot)
					};
					this.dataItems[index] = new VIPRDataItem(resultDataItem, this.context, content);
				}

			}
			//Cache information about the dataItems 
			this.dataItemInfo = this._getDataItemInfo();

		}

		destroy() {
			this.dataItems.forEach((dataItem, index) => {
				dataItem.destroy();
				delete this.dataItems[index];
			});
			for(const key in this.viprDataPoints) {
				this.viprDataPoints[key].destroy();
				delete this.viprDataPoints[key];
			}
			this.dataItems = null;
			this.viprDataPoints = null;
			this.context = null;
		}

		_getSlot(resultDataItem) {
			const slot = this.slots.find(slot => {
				if (SlotAPIHelper.isMultiMeasuresValueSlot(slot) && resultDataItem.getDataItemList()[0].getId() === SlotAPIHelper.MULTI_MEASURES_VALUE) {
					return true;
				}

				const foundDataItem = slot.getDataItemList().find(dataItem => {
					return dataItem.getId() === resultDataItem.getDataItemList()[0].getId();
				});
				return foundDataItem ;
			});
			return slot;
		}

		getId() {
			return this.id;
		}

		// implement IDataSet
		getDataItem(index) {
			return index < this.dataItems.length ? this.dataItems[index] : null;
		}

		getDataItemCount() {
			return this.dataItems.length;
		}

		getDataPointCount(){
			return this.queryResult.getRowCount();
		}

		getDataPoint( index ){
			if (index < this.queryResult.getRowCount()) {
				let dataPoint = this.viprDataPoints[index];
				//No IDatapoint exists for this key yet....create an object and create the API with asIDataPoint.
				if (!dataPoint) {
					dataPoint = new VIPRDataPoint(index, this, this.dataItemInfo, this.context);
					this.viprDataPoints[index] = dataPoint;
				}
				return dataPoint;
			}
			return null;
		}

		_getDataItemInfo() {
			const dataItemInfo = {
				isLatLong: {},
				formatter: {}
			};

			this.dataItems.forEach((dataItem, resultItemIndex) => {
				const slotDef = this.slots[resultItemIndex] && this.slots[resultItemIndex].getDefinition();
				const subType = slotDef && slotDef.getSubType();
				if (subType === 'longitude' || subType === 'latitude') {
					dataItemInfo.isLatLong[resultItemIndex]=true;
				}
				if (dataItem.getFormatter) {
					dataItemInfo.formatter[resultItemIndex] = dataItem.getFormatter && dataItem.getFormatter();
				}
			});

			return dataItemInfo;
		}
	}

	return VIPRDataSet;
});